import { useState, useEffect, useMemo, useRef } from 'react';
import { ACCOUNT_TYPES, inferAccountType } from '../../constants/accountTypes';
import { formatCurrency, generateId, parseAmount, formatAmountInput } from '../../utils/formatting';
import { printSection } from '../../utils/printHelper';

const API_URL = import.meta.env.VITE_API_URL;

// 家事按分自動生成仕訳の識別子
const AUTO_APPORTIONMENT_DESCRIPTION = '家事按分（自動生成）';

export const ETaxTransferIndividual = ({ accounts, journals, setJournals, getAccountBalance, currentFiscalYear, user, fileSystem, showAlert }) => {
    const [activeTab, setActiveTab] = useState('pl');
    const [copyMessage, setCopyMessage] = useState(null);
    const [fixedAssets, setFixedAssets] = useState([]);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const contentRef = useRef(null);
    // 青色申告特別控除額の設定（65万円 or 55万円 or 10万円）
    // 65万円: e-Tax申告 または 電子帳簿保存法対応の場合
    // 55万円: 複式簿記で記帳しているが上記条件を満たさない場合
    // 10万円: 簡易簿記の場合（このアプリでは対象外）
    const [blueDeductionType, setBlueDeductionType] = useState('65');
    const [blue65RequirementMet, setBlue65RequirementMet] = useState(false);
    const [isLoadingDeductions, setIsLoadingDeductions] = useState(true);
    // 期首残高（前年度からの繰越）
    const [openingBalances, setOpeningBalances] = useState({});
    const [isLoadingPrevYear, setIsLoadingPrevYear] = useState(true);
    // 繰越純損失
    const [carryoverLoss, setCarryoverLoss] = useState(0);
    // 家事按分表示用
    const [apportionmentRatios, setApportionmentRatios] = useState({});

    // deductions.jsonから青色申告特別控除の設定・繰越損失を読み込み
    useEffect(() => {
        if (fileSystem && currentFiscalYear) {
            setIsLoadingDeductions(true);
            fileSystem.getDeductions(currentFiscalYear).then(data => {
                if (data?.blueDeductionType) {
                    setBlueDeductionType(data.blueDeductionType);
                }
                if (data?.blue65RequirementMet !== undefined) {
                    setBlue65RequirementMet(data.blue65RequirementMet);
                }
                if (data?.carryoverLoss !== undefined) {
                    setCarryoverLoss(data.carryoverLoss);
                }
            }).catch(err => {
                console.error('Failed to load deductions:', err);
            }).finally(() => {
                setIsLoadingDeductions(false);
            });
        }
    }, [fileSystem, currentFiscalYear]);

    // 家事按分設定の読み込み（表示用）
    // 注意: 自動仕訳生成はApp.jsx起動時に実行される
    useEffect(() => {
        const loadApportionment = async () => {
            if (!fileSystem || !currentFiscalYear) return;

            try {
                const data = await fileSystem.getApportionment(currentFiscalYear);
                if (!data || !data.ratios) return;

                const ratios = {};
                Object.entries(data.ratios).forEach(([accountId, setting]) => {
                    const br = Number(setting.businessRatio);
                    if (!isNaN(br) && br > 0 && br < 100) {
                        ratios[accountId] = br;
                    }
                });
                setApportionmentRatios(ratios);
            } catch (err) {
                console.error('Failed to load apportionment:', err);
            }
        };

        loadApportionment();
    }, [fileSystem, currentFiscalYear]);

    // 前年度データを読み込み、期首残高を計算
    useEffect(() => {
        const loadPrevYearData = async () => {
            if (!fileSystem || !currentFiscalYear || !user?.email) {
                setIsLoadingPrevYear(false);
                return;
            }

            const prevYear = currentFiscalYear - 1;
            setIsLoadingPrevYear(true);

            try {
                // 前年度の仕訳データを取得
                const response = await fetch(
                    `${API_URL}?action=getJournals&type=kojin&year=${prevYear}&user=${encodeURIComponent(user.email)}`
                );

                if (!response.ok) {
                    // 前年度データがない（初年度）
                    setOpeningBalances({});
                    setIsLoadingPrevYear(false);
                    return;
                }

                const data = await response.json();
                if (!data.journals || !data.journals.trim()) {
                    // 前年度に仕訳データがない
                    setOpeningBalances({});
                    setIsLoadingPrevYear(false);
                    return;
                }

                // 前年度の仕訳から残高を計算
                const prevJournals = fileSystem.csvToJournals(data.journals, accounts).journals;
                const balances = {};

                // 科目ごとの借方・貸方合計を計算
                prevJournals.forEach(journal => {
                    (journal.lines || []).forEach(line => {
                        if (!balances[line.accountId]) {
                            balances[line.accountId] = { debit: 0, credit: 0 };
                        }
                        if (line.type === 'debit') {
                            balances[line.accountId].debit += line.amount;
                        } else {
                            balances[line.accountId].credit += line.amount;
                        }
                    });
                });

                // BS科目の期末残高を計算
                const openingBals = {};
                let prevNetIncome = 0; // 前年度の所得金額
                let prevOwnerDeposit = 0; // 前年度の事業主借
                let prevOwnerWithdrawal = 0; // 前年度の事業主貸

                accounts.forEach(acc => {
                    const accType = inferAccountType(acc);
                    const bal = balances[acc.id] || { debit: 0, credit: 0 };

                    // 資産・費用は借方 - 貸方、負債・純資産・収益は貸方 - 借方
                    let balance;
                    if (accType === ACCOUNT_TYPES.ASSET || accType === ACCOUNT_TYPES.EXPENSE) {
                        balance = bal.debit - bal.credit;
                    } else {
                        balance = bal.credit - bal.debit;
                    }

                    // 収益・費用から所得金額を計算
                    if (accType === ACCOUNT_TYPES.REVENUE) {
                        prevNetIncome += balance;
                    } else if (accType === ACCOUNT_TYPES.EXPENSE) {
                        prevNetIncome -= balance;
                    }

                    // 事業主借・事業主貸を記録
                    if (acc.name.includes('事業主借')) {
                        prevOwnerDeposit = balance;
                    } else if (acc.name.includes('事業主貸')) {
                        prevOwnerWithdrawal = balance;
                    }

                    // BS科目のみ期首残高として保持
                    if ([ACCOUNT_TYPES.ASSET, ACCOUNT_TYPES.LIABILITY, ACCOUNT_TYPES.EQUITY].includes(accType)) {
                        openingBals[acc.id] = balance;
                    }
                });

                // 元入金の計算: 前年度期末の元入金 + 前年度の所得金額 + 前年度の事業主借 − 前年度の事業主貸
                const capitalAccount = accounts.find(a => a.name.includes('元入金'));
                if (capitalAccount) {
                    const prevCapital = openingBals[capitalAccount.id] || 0;
                    const newCapital = prevCapital + prevNetIncome + prevOwnerDeposit - prevOwnerWithdrawal;
                    openingBals[capitalAccount.id] = newCapital;

                    // 事業主借・事業主貸は期首で0にリセット
                    accounts.forEach(acc => {
                        if (acc.name.includes('事業主借') || acc.name.includes('事業主貸')) {
                            openingBals[acc.id] = 0;
                        }
                    });
                }

                setOpeningBalances(openingBals);
            } catch (err) {
                console.error('Failed to load previous year data:', err);
                setOpeningBalances({});
            } finally {
                setIsLoadingPrevYear(false);
            }
        };

        loadPrevYearData();
    }, [fileSystem, currentFiscalYear, user, accounts]);

    const EXPENSE_CATEGORIES = [
        { id: 'rent_tax', num: 8, name: '租税公課', keywords: ['租税公課', '税金', '印紙'], column: 'left' },
        { id: 'packing', num: 9, name: '荷造運賃', keywords: ['荷造', '運賃', '発送', '配送'], column: 'left' },
        { id: 'utility', num: 10, name: '水道光熱費', keywords: ['水道', '光熱', '電気', 'ガス'], column: 'left' },
        { id: 'travel', num: 11, name: '旅費交通費', keywords: ['旅費', '交通費', '出張'], column: 'left' },
        { id: 'communication', num: 12, name: '通信費', keywords: ['通信', '電話', 'インターネット'], column: 'left' },
        { id: 'advertising', num: 13, name: '広告宣伝費', keywords: ['広告', '宣伝', '販促'], column: 'left' },
        { id: 'entertainment', num: 14, name: '接待交際費', keywords: ['接待', '交際', '会議費'], column: 'left' },
        { id: 'insurance', num: 15, name: '損害保険料', keywords: ['損害保険', '火災保険'], column: 'left' },
        { id: 'repair', num: 16, name: '修繕費', keywords: ['修繕', '修理'], column: 'left' },
        { id: 'consumables', num: 17, name: '消耗品費', keywords: ['消耗品', '事務用品'], column: 'middle' },
        { id: 'depreciation', num: 18, name: '減価償却費', keywords: ['減価償却'], column: 'middle' },
        { id: 'welfare', num: 19, name: '福利厚生費', keywords: ['福利厚生'], column: 'middle' },
        { id: 'salary', num: 20, name: '給料賃金', keywords: ['給料', '賃金', '給与'], column: 'middle' },
        { id: 'outsourcing', num: 21, name: '外注工賃', keywords: ['外注', '業務委託'], column: 'middle' },
        { id: 'interest', num: 22, name: '利子割引料', keywords: ['利子', '割引', '支払利息'], column: 'middle' },
        { id: 'rent', num: 23, name: '地代家賃', keywords: ['地代', '家賃', '賃借'], column: 'middle' },
        { id: 'bad_debt', num: 24, name: '貸倒金', keywords: ['貸倒'], column: 'middle' },
        { id: 'misc', num: 31, name: '雑費', keywords: ['雑費'], column: 'middle' }
    ];

    const mapToExpenseCategory = (accountName) => {
        for (const cat of EXPENSE_CATEGORIES) {
            if (cat.keywords.some(kw => accountName.includes(kw))) {
                return cat.id;
            }
        }
        return 'misc';
    };

    const circledNum = (n) => {
        const chars = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳㉑㉒㉓㉔㉕㉖㉗㉘㉙㉚㉛㉜㉝㉞㉟㊱㊲㊳㊴㊵㊶㊷㊸㊹㊺';
        return n >= 1 && n <= 50 ? chars[n - 1] : String(n);
    };

    const incomeStatementData = useMemo(() => {
        const revenues = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
            .map(a => ({ name: a.name, id: a.id, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const expenses = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
            .map(a => ({ name: a.name, id: a.id, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const totalRevenue = revenues.reduce((sum, a) => sum + a.balance, 0);
        const totalExpense = expenses.reduce((sum, a) => sum + a.balance, 0);

        return { revenues, expenses, totalRevenue, totalExpense };
    }, [accounts, getAccountBalance]);

    const totalRevenue = incomeStatementData.totalRevenue;

    const expenseByCategory = useMemo(() => {
        const result = {};
        EXPENSE_CATEGORIES.forEach(cat => { result[cat.id] = 0; });
        incomeStatementData.expenses.forEach(exp => {
            const categoryId = mapToExpenseCategory(exp.name);
            result[categoryId] += exp.balance;
        });
        return result;
    }, [incomeStatementData.expenses]);

    const { currentPurchases, beginningInventory, endingInventory, costOfSales } = useMemo(() => {
        const cp = accounts
            .filter(a => a.name === '仕入高' || a.name === '仕入')
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);
        const inventoryAccount = accounts.find(a => a.name.includes('繰越商品') || a.name === '商品');
        const bi = inventoryAccount ? (openingBalances[inventoryAccount.id] ?? 0) : 0;
        const ei = inventoryAccount ? getAccountBalance(inventoryAccount.id) : 0;
        return {
            currentPurchases: cp,
            beginningInventory: bi,
            endingInventory: ei,
            costOfSales: bi + cp - ei
        };
    }, [accounts, getAccountBalance, openingBalances]);

    // totalPurchases は後方互換のため costOfSales を使用
    const totalPurchases = costOfSales;

    const totalExpenses = incomeStatementData.totalExpense;
    const grossProfit = totalRevenue - costOfSales;
    const netIncome = grossProfit - totalExpenses;

    const monthlyData = useMemo(() => {
        const months = [];
        for (let m = 1; m <= 12; m++) {
            const monthStr = String(m).padStart(2, '0');
            const startDate = `${currentFiscalYear}-${monthStr}-01`;
            const lastDay = new Date(currentFiscalYear, m, 0).getDate();
            const endDate = `${currentFiscalYear}-${monthStr}-${String(lastDay).padStart(2, '0')}`;
            let sales = 0, purchases = 0, otherExpense = 0;
            (journals || []).forEach(j => {
                if (j.date >= startDate && j.date <= endDate) {
                    (j.lines || []).forEach(line => {
                        const acc = accounts.find(a => String(a.id) === String(line.accountId));
                        if (!acc) return;
                        if (inferAccountType(acc) === ACCOUNT_TYPES.REVENUE) {
                            sales += line.type === 'credit' ? line.amount : -line.amount;
                        } else if (inferAccountType(acc) === ACCOUNT_TYPES.EXPENSE && (acc.name === '仕入高' || acc.name === '仕入' || acc.name.includes('仕入'))) {
                            purchases += line.type === 'debit' ? line.amount : -line.amount;
                        } else if (inferAccountType(acc) === ACCOUNT_TYPES.EXPENSE) {
                            otherExpense += line.type === 'debit' ? line.amount : -line.amount;
                        }
                    });
                }
            });
            months.push({ month: m, sales, purchases, otherExpense });
        }
        return months;
    }, [journals, accounts, currentFiscalYear]);

    const balanceSheetData = useMemo(() => {
        // 期首残高と期末残高の両方を含める
        const assets = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.ASSET)
            .map(a => ({
                name: a.name,
                id: a.id,
                opening: openingBalances[a.id] || 0,
                closing: getAccountBalance(a.id)
            }))
            .filter(a => a.opening !== 0 || a.closing !== 0);
        const liabilities = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.LIABILITY)
            .map(a => ({
                name: a.name,
                id: a.id,
                opening: openingBalances[a.id] || 0,
                closing: getAccountBalance(a.id)
            }))
            .filter(a => a.opening !== 0 || a.closing !== 0);
        const equity = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EQUITY)
            .map(a => ({
                name: a.name,
                id: a.id,
                opening: openingBalances[a.id] || 0,
                closing: getAccountBalance(a.id)
            }));

        const totalAssetsOpening = assets.reduce((sum, a) => sum + a.opening, 0);
        const totalAssetsClosing = assets.reduce((sum, a) => sum + a.closing, 0);
        const totalLiabilitiesOpening = liabilities.reduce((sum, a) => sum + a.opening, 0);
        const totalLiabilitiesClosing = liabilities.reduce((sum, a) => sum + a.closing, 0);
        const totalEquityOpening = equity.reduce((sum, a) => sum + a.opening, 0);
        const totalEquityClosing = equity.reduce((sum, a) => sum + a.closing, 0) + netIncome;

        return {
            assets, liabilities, equity,
            totalAssetsOpening, totalAssetsClosing,
            totalLiabilitiesOpening, totalLiabilitiesClosing,
            totalEquityOpening, totalEquityClosing
        };
    }, [accounts, getAccountBalance, netIncome, openingBalances]);

    useEffect(() => {
        const loadAssets = async () => {
            try {
                const response = await fetch(`${API_URL}?action=getAssets&type=kojin&user=${encodeURIComponent(user?.email || '')}`);
                if (response.ok) {
                    const data = await response.json();
                    setFixedAssets(data.assets || []);
                }
            } catch (err) {
                console.error('Failed to load assets:', err);
            }
        };
        loadAssets();
    }, [user]);

    const copyToClipboard = (value) => {
        const text = String(Math.abs(value || 0));
        navigator.clipboard.writeText(text).then(() => {
            setCopyMessage('コピーしました');
            setTimeout(() => setCopyMessage(null), 1000);
        });
    };

    // 青色申告特別控除額の計算
    const effectiveMax = blueDeductionType === '65'
        ? (blue65RequirementMet ? 650_000 : 550_000)
        : blueDeductionType === '55' ? 550_000
        : 100_000;
    const blueDeduction = Math.min(netIncome > 0 ? effectiveMax : 0, Math.max(0, netIncome));
    const finalIncome = Math.max(0, netIncome - blueDeduction - carryoverLoss);

    const handleBlue65RequirementChange = async (checked) => {
        setBlue65RequirementMet(checked);
        if (fileSystem && currentFiscalYear) {
            try {
                const existing = await fileSystem.getDeductions(currentFiscalYear) || {};
                await fileSystem.saveDeductions(currentFiscalYear, {
                    ...existing,
                    blue65RequirementMet: checked,
                });
            } catch (err) {
                console.error('Failed to save blue65RequirementMet:', err);
            }
        }
    };

    const handleCarryoverLossChange = async (value) => {
        const num = Number(value) || 0;
        setCarryoverLoss(num);
        if (fileSystem && currentFiscalYear) {
            try {
                const existing = await fileSystem.getDeductions(currentFiscalYear) || {};
                await fileSystem.saveDeductions(currentFiscalYear, {
                    ...existing,
                    carryoverLoss: num,
                });
            } catch (err) {
                console.error('Failed to save carryoverLoss:', err);
            }
        }
    };

    // 控除額設定を保存（deductions.jsonに含めて保存）
    const handleBlueDeductionChange = async (type) => {
        setBlueDeductionType(type);
        if (fileSystem && currentFiscalYear) {
            try {
                // 既存のdeductionsを取得して、blueDeductionTypeを追加/更新
                const existingDeductions = await fileSystem.getDeductions(currentFiscalYear) || {};
                await fileSystem.saveDeductions(currentFiscalYear, {
                    ...existingDeductions,
                    blueDeductionType: type
                });
            } catch (err) {
                console.error('Failed to save blue deduction type:', err);
            }
        }
    };

    const leftExpenses = EXPENSE_CATEGORIES.filter(c => c.column === 'left');
    const middleExpenses = EXPENSE_CATEGORIES.filter(c => c.column === 'middle');

    const exportToPdf = async () => {
        setIsExportingPdf(true);
        try {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'mm', 'a4');
            const tabs = ['pl', 'monthly', 'depreciation', 'bs'];
            const originalTab = activeTab;
            for (let i = 0; i < tabs.length; i++) {
                setActiveTab(tabs[i]);
                await new Promise(r => setTimeout(r, 100));
                if (i > 0) doc.addPage();
                if (contentRef.current) {
                    const canvas = await window.html2canvas(contentRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' });
                    const imgData = canvas.toDataURL('image/jpeg', 0.95);
                    const imgWidth = 180, imgHeight = (canvas.height * imgWidth) / canvas.width;
                    const scale = imgHeight > 267 ? 267 / imgHeight : 1;
                    doc.addImage(imgData, 'JPEG', 15, 15, imgWidth * scale, imgHeight * scale);
                }
            }
            setActiveTab(originalTab);
            doc.save(`e-Tax転記用_個人_${currentFiscalYear}年.pdf`);
        } catch (e) {
            console.error(e);
            alert('PDF出力に失敗しました');
        } finally {
            setIsExportingPdf(false);
        }
    };

    const cellStyle = { padding: '6px 8px', borderBottom: '1px solid #ddd', fontSize: '13px' };
    const numCellStyle = { ...cellStyle, textAlign: 'center', width: '36px', background: '#f5f5f5', fontWeight: 'bold' };
    const copyCellStyle = { ...cellStyle, textAlign: 'right', cursor: 'pointer', background: '#fffef0', width: '90px' };
    const labelCellStyle = { ...cellStyle };

    const CopyCell = ({ value, style = {} }) => (
        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'right', cursor: 'pointer', backgroundColor: '#fffef0', ...style }}
            onClick={() => copyToClipboard(value)} title="クリックでコピー">
            {formatCurrency(value || 0)}
        </td>
    );

    const LabelCell = ({ children, style = {} }) => (
        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', ...style }}>{children}</td>
    );

    return (
        <div>
            {copyMessage && (
                <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, background: '#333', color: '#fff', padding: '20px 40px', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold' }}>
                    {copyMessage}
                </div>
            )}

            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h2>e-Tax転記用（個人事業主）</h2>
                    <p style={{ color: '#666', fontSize: '14px' }}>{currentFiscalYear}年分（{currentFiscalYear}/1/1〜{currentFiscalYear}/12/31）</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary" onClick={() => window.print()}>
                        印刷
                    </button>
                    <button className="btn btn-primary" onClick={exportToPdf} disabled={isExportingPdf}>
                        {isExportingPdf ? 'PDF出力中...' : 'PDF出力'}
                    </button>
                </div>
            </div>

            <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                金額欄をクリックでコピー。e-Taxに貼り付けてください。
            </div>

            <div className="card" style={{ marginBottom: '20px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>青色申告特別控除額:</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input type="radio" name="blueDeduction" value="65" checked={blueDeductionType === '65'} onChange={() => handleBlueDeductionChange('65')} />
                            65万円
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input type="radio" name="blueDeduction" value="55" checked={blueDeductionType === '55'} onChange={() => handleBlueDeductionChange('55')} />
                            55万円
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <input type="radio" name="blueDeduction" value="10" checked={blueDeductionType === '10'} onChange={() => handleBlueDeductionChange('10')} />
                            10万円
                        </label>
                    </div>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: 0 }}>
                    65万円: e-Tax申告 または 電子帳簿保存を行う場合　/　55万円: 複式簿記のみの場合　/　10万円: 簡易簿記の場合
                </p>
                {blueDeductionType === '65' && (
                    <div style={{marginTop: '8px', padding: '12px', background: '#fff9e6', borderRadius: '6px', border: '1px solid #ffc107'}}>
                        <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer'}}>
                            <input type="checkbox" checked={blue65RequirementMet}
                                onChange={e => handleBlue65RequirementChange(e.target.checked)}
                                style={{marginTop: '2px'}} />
                            <span style={{fontSize: '13px'}}>e-Tax による電子申告を行う、または優良な電子帳簿保存（JIIMA 認証等）を実施している</span>
                        </label>
                        {!blue65RequirementMet && (
                            <p style={{color: '#e65100', fontSize: '12px', margin: '8px 0 0 24px'}}>未確認のため、55万円で計算しています。</p>
                        )}
                    </div>
                )}
            </div>

            <div className="card">
                <div className="tab-container" style={{ borderBottom: '1px solid #ddd', marginBottom: '20px' }}>
                    <div className="tab-list">
                        <div className={`tab-item ${activeTab === 'pl' ? 'active' : ''}`} onClick={() => setActiveTab('pl')}>損益計算書</div>
                        <div className={`tab-item ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>月別売上・仕入</div>
                        <div className={`tab-item ${activeTab === 'depreciation' ? 'active' : ''}`} onClick={() => setActiveTab('depreciation')}>減価償却費</div>
                        <div className={`tab-item ${activeTab === 'bs' ? 'active' : ''}`} onClick={() => setActiveTab('bs')}>貸借対照表</div>
                    </div>
                </div>

                <div ref={contentRef}>
                    {/* Profit & Loss Statement */}
                    <div className="etax-print-section" style={{ display: activeTab === 'pl' ? 'block' : 'none' }}>
                        <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>
                            損益計算書（1ページ目）
                            <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '12px', color: '#666' }}>
                                令和{currentFiscalYear - 2018}年分
                            </span>
                        </h3>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', border: '1px solid #999' }}>
                            <tbody>
                                <tr style={{ background: '#f0f0f0' }}>
                                    <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>売上（収入）金額</td>
                                    <td style={numCellStyle}>{circledNum(1)}</td>
                                    <td style={copyCellStyle} onClick={() => copyToClipboard(totalRevenue)} title="クリックでコピー">
                                        {formatCurrency(totalRevenue)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', border: '1px solid #999' }}>
                            <tbody>
                                <tr>
                                    <td style={labelCellStyle}>期首商品棚卸高</td>
                                    <td style={numCellStyle}>{circledNum(2)}</td>
                                    <td style={copyCellStyle} onClick={() => copyToClipboard(beginningInventory)} title="クリックでコピー">{formatCurrency(beginningInventory)}</td>
                                </tr>
                                <tr>
                                    <td style={labelCellStyle}>仕入金額（売上原価）</td>
                                    <td style={numCellStyle}>{circledNum(3)}</td>
                                    <td style={copyCellStyle} onClick={() => copyToClipboard(currentPurchases)} title="クリックでコピー">
                                        {formatCurrency(currentPurchases)}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={labelCellStyle}>小計（+）</td>
                                    <td style={numCellStyle}>{circledNum(4)}</td>
                                    <td style={copyCellStyle} onClick={() => copyToClipboard(beginningInventory + currentPurchases)} title="クリックでコピー">
                                        {formatCurrency(beginningInventory + currentPurchases)}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={labelCellStyle}>期末商品棚卸高</td>
                                    <td style={numCellStyle}>{circledNum(5)}</td>
                                    <td style={copyCellStyle} onClick={() => copyToClipboard(endingInventory)} title="クリックでコピー">{formatCurrency(endingInventory)}</td>
                                </tr>
                                <tr style={{ background: '#f0f0f0' }}>
                                    <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>差引原価（-）</td>
                                    <td style={numCellStyle}>{circledNum(6)}</td>
                                    <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(costOfSales)} title="クリックでコピー">
                                        {formatCurrency(costOfSales)}
                                    </td>
                                </tr>
                                <tr style={{ background: '#e3f2fd' }}>
                                    <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>差引金額（売上総利益）</td>
                                    <td style={numCellStyle}>{circledNum(7)}</td>
                                    <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(grossProfit)} title="クリックでコピー">
                                        {formatCurrency(grossProfit)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Expenses */}
                        <div style={{ border: '1px solid #999', marginBottom: '16px' }}>
                            <div style={{ background: '#e8e8e8', padding: '8px', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #999' }}>
                                経　　費
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ flex: 1, borderRight: '1px solid #ccc' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {leftExpenses.map(cat => (
                                                <tr key={cat.id}>
                                                    <td style={labelCellStyle}>{cat.name}</td>
                                                    <td style={numCellStyle}>{circledNum(cat.num)}</td>
                                                    <td style={copyCellStyle} onClick={() => copyToClipboard(expenseByCategory[cat.id])} title="クリックでコピー">
                                                        {formatCurrency(expenseByCategory[cat.id])}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ flex: 1, borderRight: '1px solid #ccc' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {middleExpenses.filter(c => c.num <= 24).map(cat => (
                                                <tr key={cat.id}>
                                                    <td style={labelCellStyle}>{cat.name}</td>
                                                    <td style={numCellStyle}>{circledNum(cat.num)}</td>
                                                    <td style={copyCellStyle} onClick={() => copyToClipboard(expenseByCategory[cat.id])} title="クリックでコピー">
                                                        {formatCurrency(expenseByCategory[cat.id])}
                                                    </td>
                                                </tr>
                                            ))}
                                            {[25, 26, 27, 28, 29, 30].map(n => (
                                                <tr key={n}>
                                                    <td style={{ ...labelCellStyle, color: '#999' }}></td>
                                                    <td style={numCellStyle}>{circledNum(n)}</td>
                                                    <td style={{ ...copyCellStyle, color: '#999' }}>-</td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td style={labelCellStyle}>雑費</td>
                                                <td style={numCellStyle}>{circledNum(31)}</td>
                                                <td style={copyCellStyle} onClick={() => copyToClipboard(expenseByCategory['misc'])} title="クリックでコピー">
                                                    {formatCurrency(expenseByCategory['misc'])}
                                                </td>
                                            </tr>
                                            <tr style={{ background: '#fff3e0' }}>
                                                <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>計</td>
                                                <td style={numCellStyle}>{circledNum(32)}</td>
                                                <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(totalExpenses)} title="クリックでコピー">
                                                    {formatCurrency(totalExpenses)}
                                                </td>
                                            </tr>
                                            <tr style={{ background: '#e3f2fd' }}>
                                                <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>差引金額（-）</td>
                                                <td style={numCellStyle}>{circledNum(33)}</td>
                                                <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(netIncome)} title="クリックでコピー">
                                                    {formatCurrency(netIncome)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            <tr><td colSpan="3" style={{ ...labelCellStyle, fontSize: '11px', color: '#666', background: '#f8f8f8', textAlign: 'center' }}>各種引当金・繰入額等</td></tr>
                                            <tr><td style={labelCellStyle}>貸倒引当金</td><td style={numCellStyle}>{circledNum(34)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr><td style={labelCellStyle}>戻</td><td style={numCellStyle}>{circledNum(35)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr><td style={{ ...labelCellStyle, color: '#999' }}></td><td style={numCellStyle}>{circledNum(36)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr style={{ background: '#f0f0f0' }}><td style={{ ...labelCellStyle, fontWeight: 'bold' }}>計</td><td style={numCellStyle}>{circledNum(37)}</td><td style={copyCellStyle} onClick={() => copyToClipboard(0)}>0</td></tr>
                                            <tr><td colSpan="3" style={{ ...labelCellStyle, fontSize: '11px', color: '#666', background: '#f8f8f8', textAlign: 'center' }}>専従者給与・繰入・準備金等</td></tr>
                                            <tr><td style={labelCellStyle}>専従者給与</td><td style={numCellStyle}>{circledNum(38)}</td><td style={copyCellStyle} onClick={() => copyToClipboard(0)}>0</td></tr>
                                            <tr><td style={labelCellStyle}>貸倒引当金</td><td style={numCellStyle}>{circledNum(39)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr><td style={{ ...labelCellStyle, color: '#999' }}></td><td style={numCellStyle}>{circledNum(40)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr><td style={{ ...labelCellStyle, color: '#999' }}></td><td style={numCellStyle}>{circledNum(41)}</td><td style={{ ...copyCellStyle, color: '#999' }}>-</td></tr>
                                            <tr style={{ background: '#f0f0f0' }}><td style={{ ...labelCellStyle, fontWeight: 'bold' }}>計</td><td style={numCellStyle}>{circledNum(42)}</td><td style={copyCellStyle} onClick={() => copyToClipboard(0)}>0</td></tr>
                                            <tr style={{ background: '#fff3e0' }}>
                                                <td style={{ ...labelCellStyle, fontWeight: 'bold', fontSize: '12px' }}>青色申告特別控除前の所得金額</td>
                                                <td style={numCellStyle}>{circledNum(43)}</td>
                                                <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(netIncome)} title="クリックでコピー">
                                                    {formatCurrency(netIncome)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={labelCellStyle}>青色申告特別控除額（{blueDeductionType === '65' && !blue65RequirementMet ? '55' : blueDeductionType}万円）</td>
                                                <td style={numCellStyle}>{circledNum(44)}</td>
                                                <td style={copyCellStyle} onClick={() => copyToClipboard(blueDeduction)} title="クリックでコピー">
                                                    {formatCurrency(blueDeduction)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="3" style={{ padding: '4px 8px', borderBottom: '1px solid #ddd' }}>
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                                        <label style={{fontSize: '13px', color: '#555', minWidth: '200px'}}>前年からの繰越純損失（3年以内）</label>
                                                        <input
                                                            type="text"
                                                            value={formatAmountInput(carryoverLoss)}
                                                            onChange={e => setCarryoverLoss(parseAmount(e.target.value) || 0)}
                                                            onBlur={e => handleCarryoverLossChange(e.target.value)}
                                                            style={{textAlign: 'right', width: '120px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px'}}
                                                        />
                                                        <span style={{fontSize: '13px'}}>円</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr style={{ background: '#c8e6c9' }}>
                                                <td style={{ ...labelCellStyle, fontWeight: 'bold' }}>事業所得（繰越損失適用後）</td>
                                                <td style={numCellStyle}>{circledNum(45)}</td>
                                                <td style={{ ...copyCellStyle, fontWeight: 'bold' }} onClick={() => copyToClipboard(finalIncome)} title="クリックでコピー">
                                                    {formatCurrency(finalIncome)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Sales */}
                    <div className="etax-print-section" style={{ display: activeTab === 'monthly' ? 'block' : 'none' }}>
                        <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>月別売上（収入）金額及び仕入金額（2ページ目）</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                    <th style={{ padding: '10px', border: '1px solid #ccc' }}>月</th>
                                    <th style={{ padding: '10px', border: '1px solid #ccc' }}>売上（収入）金額</th>
                                    <th style={{ padding: '10px', border: '1px solid #ccc' }}>仕入金額</th>
                                    <th style={{ padding: '10px', border: '1px solid #ccc' }}>経費合計（仕入除く）</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyData.map(m => (
                                    <tr key={m.month}>
                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>{m.month}月</td>
                                        <CopyCell value={m.sales} style={{ border: '1px solid #ddd' }} />
                                        <CopyCell value={m.purchases} style={{ border: '1px solid #ddd' }} />
                                        <CopyCell value={m.otherExpense} style={{ border: '1px solid #ddd' }} />
                                    </tr>
                                ))}
                                <tr style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                                    <td style={{ padding: '10px', border: '1px solid #ccc', textAlign: 'center' }}>合計</td>
                                    <CopyCell value={monthlyData.reduce((s, m) => s + m.sales, 0)} style={{ border: '1px solid #ccc', fontWeight: 'bold' }} />
                                    <CopyCell value={monthlyData.reduce((s, m) => s + m.purchases, 0)} style={{ border: '1px solid #ccc', fontWeight: 'bold' }} />
                                    <CopyCell value={monthlyData.reduce((s, m) => s + m.otherExpense, 0)} style={{ border: '1px solid #ccc', fontWeight: 'bold' }} />
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Depreciation */}
                    <div className="etax-print-section" style={{ display: activeTab === 'depreciation' ? 'block' : 'none' }}>
                        <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>減価償却費の計算（3ページ目）</h3>
                        {fixedAssets.filter(a => !a.deletedAt).length > 0 ? (
                            <table id="depreciation-print-section" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#e8e8e8' }}>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>資産名</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>取得年月</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>取得価額</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>償却方法</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>耐用年数</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>本年分償却費</th>
                                        <th style={{ padding: '8px', border: '1px solid #ccc' }}>未償却残高</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fixedAssets.filter(a => !a.deletedAt).map(asset => (
                                        <tr key={asset.id}>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{asset.name}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'center' }}>{asset.acquisitionDate?.substring(0, 7).replace('-', '.')}</td>
                                            <CopyCell value={asset.acquisitionCost} style={{ border: '1px solid #ddd' }} />
                                            <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'center' }}>{asset.depreciationMethod === 'straight' ? '定額' : '定率'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd', textAlign: 'center' }}>{asset.usefulLife}</td>
                                            <CopyCell value={asset.currentYearDepreciation || 0} style={{ border: '1px solid #ddd' }} />
                                            <CopyCell value={asset.bookValue || 0} style={{ border: '1px solid #ddd' }} />
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>
                                        <td colSpan="5" style={{ padding: '10px', border: '1px solid #ccc' }}>合計</td>
                                        <CopyCell value={fixedAssets.filter(a => !a.deletedAt).reduce((s, a) => s + (a.currentYearDepreciation || 0), 0)} style={{ border: '1px solid #ccc', fontWeight: 'bold' }} />
                                        <CopyCell value={fixedAssets.filter(a => !a.deletedAt).reduce((s, a) => s + (a.bookValue || 0), 0)} style={{ border: '1px solid #ccc', fontWeight: 'bold' }} />
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#888', background: '#f9f9f9', borderRadius: '8px' }}>
                                固定資産が登録されていません
                            </div>
                        )}
                        <button
                            onClick={() => printSection('depreciation-print-section')}
                            style={{marginTop: '12px', padding: '8px 16px', background: '#607d8b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                        >
                            印刷 / PDF
                        </button>
                    </div>

                    {/* Balance Sheet */}
                    <div className="etax-print-section" style={{ display: activeTab === 'bs' ? 'block' : 'none' }}>
                        <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>貸借対照表（4ページ目）</h3>
                        <p style={{ textAlign: 'center', color: '#666', marginBottom: '16px' }}>
                            令和{currentFiscalYear - 2018}年1月1日〜令和{currentFiscalYear - 2018}年12月31日
                        </p>
                        {isLoadingPrevYear && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                前年度データを読み込み中...
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ flex: 1 }}>
                                <h4 style={{ backgroundColor: '#e8e8e8', padding: '10px', textAlign: 'center', margin: 0 }}>資産の部</h4>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>科目</th>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'right', width: '90px' }}>期首(1/1)</th>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'right', width: '90px', backgroundColor: '#fffef0' }}>期末(12/31)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {balanceSheetData.assets.map((item, i) => (
                                            <tr key={i}>
                                                <LabelCell>{item.name}</LabelCell>
                                                <CopyCell value={item.opening} style={{ backgroundColor: '#fafafa' }} />
                                                <CopyCell value={item.closing} />
                                            </tr>
                                        ))}
                                        {balanceSheetData.assets.length === 0 && <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>資産なし</td></tr>}
                                        <tr style={{ backgroundColor: '#c8e6c9', fontWeight: 'bold' }}>
                                            <LabelCell style={{ fontWeight: 'bold' }}>資産合計</LabelCell>
                                            <CopyCell value={balanceSheetData.totalAssetsOpening} style={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }} />
                                            <CopyCell value={balanceSheetData.totalAssetsClosing} style={{ fontWeight: 'bold' }} />
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h4 style={{ backgroundColor: '#e8e8e8', padding: '10px', textAlign: 'center', margin: 0 }}>負債・資本の部</h4>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>科目</th>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'right', width: '90px' }}>期首(1/1)</th>
                                            <th style={{ padding: '6px', borderBottom: '1px solid #ddd', textAlign: 'right', width: '90px', backgroundColor: '#fffef0' }}>期末(12/31)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {balanceSheetData.liabilities.map((item, i) => (
                                            <tr key={i}>
                                                <LabelCell>{item.name}</LabelCell>
                                                <CopyCell value={item.opening} style={{ backgroundColor: '#fafafa' }} />
                                                <CopyCell value={item.closing} />
                                            </tr>
                                        ))}
                                        <tr style={{ backgroundColor: '#ffecb3' }}>
                                            <LabelCell style={{ fontWeight: 'bold' }}>負債合計</LabelCell>
                                            <CopyCell value={balanceSheetData.totalLiabilitiesOpening} style={{ fontWeight: 'bold', backgroundColor: '#ffecb3' }} />
                                            <CopyCell value={balanceSheetData.totalLiabilitiesClosing} style={{ fontWeight: 'bold' }} />
                                        </tr>
                                        {balanceSheetData.equity.filter(e => e.opening !== 0 || e.closing !== 0).map((item, i) => (
                                            <tr key={i}>
                                                <LabelCell>{item.name}</LabelCell>
                                                <CopyCell value={item.opening} style={{ backgroundColor: '#fafafa' }} />
                                                <CopyCell value={item.closing} />
                                            </tr>
                                        ))}
                                        <tr style={{ backgroundColor: '#e3f2fd' }}>
                                            <LabelCell style={{ fontWeight: 'bold' }}>所得金額（青色申告特別控除前）</LabelCell>
                                            <td style={{ padding: '8px', textAlign: 'center', color: '#999' }}>-</td>
                                            <CopyCell value={netIncome} style={{ fontWeight: 'bold', color: netIncome >= 0 ? '#1b5e20' : '#c62828' }} />
                                        </tr>
                                        <tr style={{ backgroundColor: '#c8e6c9', fontWeight: 'bold' }}>
                                            <LabelCell style={{ fontWeight: 'bold' }}>負債・資本合計</LabelCell>
                                            <CopyCell value={balanceSheetData.totalLiabilitiesOpening + balanceSheetData.totalEquityOpening} style={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }} />
                                            <CopyCell value={balanceSheetData.totalLiabilitiesClosing + balanceSheetData.totalEquityClosing} style={{ fontWeight: 'bold' }} />
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ETaxTransferIndividual;
